import { useState, useEffect } from 'react';
import { Smartphone, Monitor, Film, ChevronDown, ChevronUp, RotateCcw, Wand2, Sliders, X, Check, Save, FileImage } from 'lucide-react';
import { showToast } from '@/lib/toast';
import { ResizeSettings, BACKGROUND_REMOVAL_FORMATS } from '../types';

interface SettingsPanelProps {
  settings: ResizeSettings;
  onUpdateSettings: (key: keyof ResizeSettings, value: any) => void;
  onClose?: () => void;
  onSaveSettings?: (settings: ResizeSettings) => void;
}

// Presets predefinidos para diferentes casos de uso
const presets = [
  { name: 'Instagram Post', icon: Smartphone, width: 1080, height: 1080, quality: 0.9 },
  { name: 'Instagram Story', icon: Smartphone, width: 1080, height: 1920, quality: 0.9 },
  { name: 'Facebook Cover', icon: Monitor, width: 1200, height: 630, quality: 0.9 },
  { name: 'YouTube Thumbnail', icon: Film, width: 1280, height: 720, quality: 0.9 },
  { name: 'Twitter Header', icon: Monitor, width: 1500, height: 500, quality: 0.9 },
  { name: 'HD Wallpaper', icon: Monitor, width: 1920, height: 1080, quality: 0.95 },
];

export default function SettingsPanel({ 
  settings, 
  onUpdateSettings, 
  onClose, 
  onSaveSettings
}: SettingsPanelProps) {
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [savedSettings, setSavedSettings] = useState<ResizeSettings | null>(null);
  const showBackgroundRemoval = BACKGROUND_REMOVAL_FORMATS.includes(settings.format as any);

  // Carregar configurações salvas ao montar o componente
  useEffect(() => {
    const saved = localStorage.getItem('converterPro_imageSettings');
    if (saved) {
      try {
        const parsedSettings = JSON.parse(saved);
        setSavedSettings(parsedSettings);
      } catch (error) {
        console.error('Erro ao carregar configurações salvas:', error);
      }
    } else {
      // Se não há configurações salvas, marcar as atuais como base
      setSavedSettings({ ...settings });
    }
  }, []);

  // Carregar preset selecionado ao montar
  useEffect(() => {
    const savedPreset = localStorage.getItem('converterPro_selectedPreset');
    if (savedPreset) {
      setSelectedPreset(savedPreset);
    }
  }, []);

  // Inicializar savedSettings se não existir
  useEffect(() => {
    if (!savedSettings && Object.keys(settings).length > 0) {
      setSavedSettings({ ...settings });
    }
  }, [settings, savedSettings]);

  // Detectar mudanças nas configurações
  useEffect(() => {
    if (savedSettings) {
      const hasChanges = Object.keys(settings).some(key => {
        const currentValue = settings[key as keyof ResizeSettings];
        const savedValue = savedSettings[key as keyof ResizeSettings];
        return JSON.stringify(currentValue) !== JSON.stringify(savedValue);
      });
      setHasUnsavedChanges(hasChanges);
    }
  }, [settings, savedSettings]);

  const applyPreset = (preset: typeof presets[0]) => {
    onUpdateSettings('width', preset.width);
    onUpdateSettings('height', preset.height);
    onUpdateSettings('quality', preset.quality);
    onUpdateSettings('maintainAspectRatio', false);
    onUpdateSettings('removeBackground', false);
    setSelectedPreset(preset.name);
    
    // Salvar preset selecionado no localStorage
    localStorage.setItem('converterPro_selectedPreset', preset.name);
  };

  const resetToDefaults = () => {
    const defaultSettings = {
      width: 800,
      height: 600,
      quality: 0.9,
      maintainAspectRatio: false,
      format: 'original',
      removeBackground: false
    };
    
    // Aplicar configurações padrão
    Object.keys(defaultSettings).forEach(key => {
      onUpdateSettings(key as keyof ResizeSettings, defaultSettings[key as keyof typeof defaultSettings]);
    });
    
    setSelectedPreset(null);
    
    // Limpar dados salvos
    localStorage.removeItem('converterPro_selectedPreset');
    localStorage.removeItem('converterPro_imageSettings');
    setSavedSettings(defaultSettings as ResizeSettings);
  };

  // Função para salvar configurações
  const handleSaveSettings = () => {
    try {
      // Salvar no localStorage
      localStorage.setItem('converterPro_imageSettings', JSON.stringify(settings));
      
      // Atualizar estado
      setSavedSettings({ ...settings });
      setHasUnsavedChanges(false);
      
      // Toast
      showToast('✅ Configurações salvas e aplicadas');

      // Callback opcional para o componente pai
      if (onSaveSettings) {
        onSaveSettings(settings);
      }

      // Fechar modal se disponível
      if (onClose) onClose();
      
      console.log('✅ Configurações salvas com sucesso!', settings);
      
    } catch (error) {
      console.error('❌ Erro ao salvar configurações:', error);
      alert('Erro ao salvar configurações. Tente novamente.');
    }
  };

  // Debug: Verificar estados
  useEffect(() => {
    console.log('🔧 Debug SettingsPanel:', {
      settings,
      savedSettings,
      hasUnsavedChanges,
      selectedPreset
    });
  }, [settings, savedSettings, hasUnsavedChanges, selectedPreset]);

  // Verificar se está no modo escuro
  const isDarkMode = document.documentElement.classList.contains('dark');

  return (
    <div className={`${
      isDarkMode ? 'bg-[#383D52] border-[#4A4F64]' : 'bg-white border-[#E5E5E5]'
    } border rounded-2xl overflow-hidden shadow-xl w-full`}>
      {/* Header */}
      <div className={`p-4 border-b ${
        isDarkMode ? 'border-[#4A4F64]' : 'border-[#E5E5E5]'
      }`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
              <Sliders className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className={`text-lg font-bold ${
                isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
              }`}>
                Configurações
              </h3>
              <p className={`text-xs ${
                isDarkMode ? 'text-[#EAECF3]/70' : 'text-[#4A4A69]/70'
              }`}>
                Personalize suas conversões
                {hasUnsavedChanges && (
                  <span className="ml-2 text-orange-500">• Não salvo</span>
                )}
              </p>
            </div>
          </div>
          
          <div className="flex items-center justify-end gap-2">
            {/* Botão Salvar */}
            <button
              onClick={handleSaveSettings}
              disabled={!hasUnsavedChanges}
              className={`flex-1 sm:flex-none flex items-center justify-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                hasUnsavedChanges
                  ? 'bg-green-500 hover:bg-green-600 text-white shadow-md hover:shadow-lg'
                  : isDarkMode 
                    ? 'bg-[#4A4F64] text-[#EAECF3]/50 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
              title={hasUnsavedChanges ? 'Salvar configurações' : 'Nenhuma alteração para salvar'}
            >
              <Save className="w-4 h-4" />
              <span>Salvar</span>
              {hasUnsavedChanges && (
                <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
              )}
            </button>

            <button
              onClick={resetToDefaults}
              className={`w-10 h-10 sm:w-auto sm:h-auto sm:p-2 flex items-center justify-center rounded-lg ${
                isDarkMode ? 'bg-[#4A4F64] hover:bg-[#52576B] text-[#EAECF3]' : 'bg-[#F0F0F0] hover:bg-[#E5E5E5] text-[#4A4A69]'
              } transition-colors`}
              title="Restaurar padrões"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
            
            <button
              onClick={() => setIsAdvancedMode(!isAdvancedMode)}
              className={`flex items-center justify-center space-x-1 sm:space-x-2 px-3 py-2 rounded-lg ${
                isAdvancedMode
                  ? 'bg-purple-500/20 text-purple-300 border border-purple-400/30'
                  : isDarkMode ? 'bg-[#4A4F64] hover:bg-[#52576B] text-[#EAECF3]' : 'bg-[#F0F0F0] hover:bg-[#E5E5E5] text-[#4A4A69]'
              } transition-colors`}
            >
              <span className="text-sm font-medium hidden sm:inline">Avançado</span>
              <span className="text-sm font-medium sm:hidden">Avançado</span>
              {isAdvancedMode ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </button>

            {onClose && (
              <button
                onClick={onClose}
                className={`w-10 h-10 sm:w-auto sm:h-auto sm:p-2 flex items-center justify-center rounded-lg ${
                  isDarkMode ? 'hover:bg-red-500/20 text-red-300' : 'hover:bg-red-50 text-red-600'
                } transition-colors`}
                title="Fechar configurações"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        
      </div>
      
      <div className={`${isAdvancedMode ? 'max-h-[70vh] overflow-y-auto' : ''} p-4`}>
        {/* Presets Rápidos */}
        <div className="mb-4 sm:mb-6">
          <div className="flex items-center justify-between mb-2 sm:mb-3">
            <div className="flex items-center space-x-2">
              <Wand2 className="w-4 h-4 text-purple-500" />
              <h4 className={`font-semibold text-sm ${
                isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
              }`}>
                Presets Rápidos
              </h4>
            </div>
            
            {/* Indicador do preset ativo */}
            {selectedPreset && (
              <div className={`text-xs px-2 py-1 rounded-full ${
                isDarkMode ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-100 text-purple-600'
              }`}>
                Ativo: {selectedPreset}
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 sm:gap-2">
            {presets.slice(0, 6).map((preset, index) => (
              <button
                key={preset.name}
                onClick={() => applyPreset(preset)}
                className={`group relative p-2 sm:p-3 rounded-lg border transition-all duration-500 hover:scale-105 hover:shadow-lg ${
                  selectedPreset === preset.name
                    ? 'bg-purple-500 border-purple-400 text-white shadow-lg shadow-purple-500/25'
                    : isDarkMode 
                    ? 'bg-[#2A2E3F] border-[#4A4F64] hover:bg-[#383D52] text-[#EAECF3] hover:border-purple-400/50'
                    : 'bg-white border-[#E5E5E5] hover:bg-[#F8F9FA] text-[#4A4A69] hover:border-purple-400/50'
                }`}
                style={{ 
                  animationDelay: `${index * 50}ms`
                }}
              >
                <div className="text-center relative">
                  {/* Ícone de check quando ativo */}
                  {selectedPreset === preset.name && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-white rounded-full flex items-center justify-center">
                      <Check className="w-2.5 h-2.5 text-purple-500" />
                    </div>
                  )}
                  
                  <preset.icon className={`w-4 h-4 sm:w-5 sm:h-5 mx-auto mb-1 transition-all duration-300 ${
                    selectedPreset === preset.name 
                      ? 'text-white' 
                      : 'text-current group-hover:scale-110'
                  }`} />
                  <div className={`text-[11px] sm:text-xs font-medium leading-tight transition-colors duration-300 ${
                    selectedPreset === preset.name ? 'text-white' : 'text-current'
                  }`}>
                    {preset.name}
                  </div>
                  <div className={`text-[10px] sm:text-xs mt-0.5 sm:mt-1 transition-colors duration-300 ${
                    selectedPreset === preset.name 
                      ? 'text-purple-100' 
                      : isDarkMode ? 'text-[#EAECF3]/50' : 'text-[#4A4A69]/50'
                  }`}>
                    {preset.width}×{preset.height}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Configurações Básicas */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6 mb-4 sm:mb-6">
          {/* Dimensões */}
          <div className="space-y-2 sm:space-y-3">
            <h4 className={`font-semibold flex items-center space-x-2 text-sm ${
              isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
            }`}>
              <Monitor className="w-4 h-4" />
              <span>Dimensões</span>
            </h4>
            
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={`block text-[11px] sm:text-xs font-medium mb-1 ${
                  isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
                }`}>
                  Largura
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={settings.width}
                    onChange={(e) => onUpdateSettings('width', parseInt(e.target.value) || 0)}
                    className={`w-full px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                      isDarkMode 
                        ? 'bg-[#2A2E3F] border-[#4A4F64] text-[#EAECF3]' 
                        : 'bg-white border-[#E5E5E5] text-[#4A4A69]'
                    }`}
                    min="1"
                    placeholder="800"
                  />
                  <span className={`absolute right-2 top-1/2 transform -translate-y-1/2 text-[10px] sm:text-xs ${
                    isDarkMode ? 'text-[#EAECF3]/50' : 'text-[#4A4A69]/50'
                  }`}>
                    px
                  </span>
                </div>
              </div>
              
              <div>
                <label className={`block text-[11px] sm:text-xs font-medium mb-1 ${
                  isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
                }`}>
                  Altura
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={settings.height}
                    onChange={(e) => onUpdateSettings('height', parseInt(e.target.value) || 0)}
                    className={`w-full px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                      isDarkMode 
                        ? 'bg-[#2A2E3F] border-[#4A4F64] text-[#EAECF3]' 
                        : 'bg-white border-[#E5E5E5] text-[#4A4A69]'
                    }`}
                    min="1"
                    placeholder="600"
                  />
                  <span className={`absolute right-2 top-1/2 transform -translate-y-1/2 text-[10px] sm:text-xs ${
                    isDarkMode ? 'text-[#EAECF3]/50' : 'text-[#4A4A69]/50'
                  }`}>
                    px
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Qualidade */}
          <div className="space-y-2 sm:space-y-3">
            <h4 className={`font-semibold flex items-center space-x-2 text-sm ${
              isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
            }`}>
              <Wand2 className="w-4 h-4" />
              <span>Qualidade</span>
            </h4>
        
            <div>
              <div className="flex justify-between items-center mb-1.5 sm:mb-2">
                <label className={`text-[11px] sm:text-xs font-medium ${
                  isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
                }`}>
                  Qualidade
                </label>
                <span className={`text-[10px] sm:text-xs font-bold px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-md ${
                  settings.quality >= 0.9 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                  settings.quality >= 0.7 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' :
                  'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                }`}>
                  {Math.round(settings.quality * 100)}%
                </span>
              </div>
              
              {/* Barra de qualidade customizada */}
              <div className="relative">
                <div className={`w-full h-2 sm:h-3 rounded-full ${
                  isDarkMode ? 'bg-[#4A4F64]' : 'bg-gray-200'
                }`}>
                  <div 
                    className="h-full rounded-full bg-purple-500 transition-all duration-300 relative"
                    style={{ width: `${settings.quality * 100}%` }}
                  >
                    <div className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 bg-white rounded-full shadow-lg border-2 border-purple-500 cursor-pointer hover:scale-110 transition-transform duration-200"></div>
                  </div>
                </div>
                
                <input
                  type="range"
                  min="0.1"
                  max="1"
                  step="0.05"
                  value={settings.quality}
                  onChange={(e) => onUpdateSettings('quality', parseFloat(e.target.value))}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
              </div>
              
              {/* Indicadores de qualidade */}
              <div className="flex justify-between mt-1 sm:mt-2 text-[10px] sm:text-xs">
                <span className={`${
                  isDarkMode ? 'text-[#EAECF3]/50' : 'text-[#4A4A69]/50'
                }`}>
                  Menor tamanho
                </span>
                <span className={`${
                  isDarkMode ? 'text-[#EAECF3]/50' : 'text-[#4A4A69]/50'
                }`}>
                  Melhor qualidade
                </span>
              </div>
              
              {/* Presets de qualidade rápidos */}
              <div className="flex gap-1 mt-1.5 sm:mt-2">
                {[
                  { value: 0.6, label: 'Baixa', color: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-300' },
                  { value: 0.8, label: 'Média', color: 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-300' },
                  { value: 0.9, label: 'Alta', color: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-300' },
                  { value: 1.0, label: 'Máxima', color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300' }
                ].map((preset) => (
                  <button
                    key={preset.value}
                    onClick={() => onUpdateSettings('quality', preset.value)}
                    className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs rounded-md transition-all duration-200 hover:scale-105 ${
                      Math.abs(settings.quality - preset.value) < 0.05 
                        ? `${preset.color} ring-2 ring-purple-400` 
                        : `${preset.color} opacity-70 hover:opacity-100`
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Formato */}
          <div className="space-y-2 sm:space-y-3">
            <h4 className={`font-semibold flex items-center space-x-2 text-sm ${
              isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
            }`}>
              <FileImage className="w-4 h-4" />
              <span>Formato</span>
            </h4>
        
            <div>
              <label className={`block text-[11px] sm:text-xs font-medium mb-1 ${
                isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
              }`}>
                Formato de Saída
              </label>
              <select
                value={settings.format}
                onChange={(e) => onUpdateSettings('format', e.target.value)}
                className={`w-full px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                  isDarkMode 
                    ? 'bg-[#2A2E3F] border-[#4A4F64] text-[#EAECF3]' 
                    : 'bg-white border-[#E5E5E5] text-[#4A4A69]'
                }`}
              >
                <optgroup label="🔄 Padrão">
                  <option value="original">Manter formato original</option>
                </optgroup>
                
                <optgroup label="🌐 Formatos Web Populares">
                  <option value="png">PNG - Portable Network Graphics</option>
                  <option value="jpeg">JPEG - Joint Photographic Experts Group</option>
                  <option value="webp">WebP - Formato web moderno do Google</option>
                  <option value="avif">AVIF - AV1 Image File Format</option>
                  <option value="gif">GIF - Graphics Interchange Format</option>
                  <option value="svg">SVG - Scalable Vector Graphics</option>
                </optgroup>
                
                <optgroup label="📱 Formatos Mobile e Modernos">
                  <option value="heic">HEIC - High Efficiency Image Container</option>
                  <option value="heif">HEIF - High Efficiency Image Format</option>
                  <option value="jfif">JFIF - JPEG File Interchange Format</option>
                  <option value="jpe">JPE - JPEG Extension</option>
                </optgroup>
                
                <optgroup label="🖥️ Formatos Desktop e Sistema">
                  <option value="bmp">BMP - Bitmap</option>
                  <option value="ico">ICO - Ícone Windows</option>
                  <option value="cur">CUR - Cursor Windows</option>
                  <option value="tiff">TIFF - Tagged Image File Format</option>
                  <option value="wbmp">WBMP - Wireless Bitmap</option>
                </optgroup>
                
                <optgroup label="🎨 Formatos Profissionais">
                  <option value="psd">PSD - Photoshop Document</option>
                  <option value="hdr">HDR - High Dynamic Range</option>
                  <option value="exr">EXR - OpenEXR (Cinema/VFX)</option>
                  <option value="dds">DDS - DirectDraw Surface</option>
                  <option value="tga">TGA - Truevision Graphics Adapter</option>
                </optgroup>
                
                <optgroup label="🔬 Formatos Científicos/Técnicos">
                  <option value="rgb">RGB - Raw RGB Data</option>
                  <option value="rgba">RGBA - Raw RGBA Data</option>
                  <option value="yuv">YUV - Luminance/Chrominance</option>
                  <option value="vips">VIPS - Very Large Image Processing</option>
                  <option value="pfm">PFM - Portable Float Map</option>
                  <option value="uyvy">UYVY - YUV 4:2:2 Format</option>
                  <option value="hrz">HRZ - Slow Scan TV</option>
                  <option value="rgbo">RGBO - RGB with Opacity</option>
                </optgroup>
                
                <optgroup label="📼 Formatos Legados">
                  <option value="pcx">PCX - PiCture eXchange</option>
                  <option value="xpm">XPM - X11 Pixmap</option>
                  <option value="xbm">XBM - X11 Bitmap</option>
                  <option value="pbm">PBM - Portable Bitmap</option>
                  <option value="pgm">PGM - Portable Graymap</option>
                  <option value="ppm">PPM - Portable Pixmap</option>
                  <option value="pnm">PNM - Portable Anymap</option>
                  <option value="sun">SUN - Sun Rasterfile</option>
                  <option value="sgi">SGI - Silicon Graphics Image</option>
                  <option value="ras">RAS - Sun Raster</option>
                  <option value="pct">PCT - Macintosh PICT</option>
                  <option value="mtv">MTV - MTV Raytracer</option>
                </optgroup>
                
                <optgroup label="🔧 Formatos Especializados">
                  <option value="jp2">JP2 - JPEG 2000</option>
                  <option value="jbg">JBG - JBIG1</option>
                  <option value="jbig">JBIG - Joint Bi-level Image Group</option>
                  <option value="jps">JPS - JPEG Stereo</option>
                  <option value="sixel">SIXEL - DEC Terminal Graphics</option>
                  <option value="fax">FAX - Facsimile Image</option>
                  <option value="g3">G3 - CCITT Group 3 Fax</option>
                  <option value="g4">G4 - CCITT Group 4 Fax</option>
                  <option value="palm">PALM - Palm Pilot Image</option>
                  <option value="picon">PICON - Personal Icon</option>
                  <option value="pic">PIC - Generic Picture Format</option>
                  <option value="otb">OTB - On-the-air Bitmap</option>
                  <option value="pam">PAM - Portable Arbitrary Map</option>
                  <option value="viff">VIFF - Khoros Visualization</option>
                  <option value="xwd">XWD - X11 Window Dump</option>
                  <option value="mng">MNG - Multiple-image Network Graphics</option>
                  <option value="pcd">PCD - Kodak Photo CD</option>
                  <option value="xv">XV - XV Thumbnail</option>
                </optgroup>
                
                <optgroup label="🗂️ Formatos de Dados">
                  <option value="map">MAP - Colormap File</option>
                  <option value="pal">PAL - Color Palette</option>
                  <option value="pdb">PDB - Palm Database ImageViewer</option>
                  <option value="jif">JIF - JPEG Interchange Format</option>
                  <option value="jfi">JFI - JPEG File Interchange</option>
                  <option value="pgx">PGX - JPEG 2000 VM Format</option>
                  <option value="six">SIX - DEC SIXEL Format</option>
                  <option value="ipl">IPL - Image Processing Library</option>
                  <option value="rgf">RGF - LEGO Mindstorms</option>
                  <option value="fts">FTS - Flexible Image Transport System</option>
                </optgroup>
              </select>
            </div>
          </div>
        </div>
        
        {/* Configuração de Proporção */}
        <div className="mb-4">
          <label className={`flex items-center space-x-3 cursor-pointer ${
            isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
          }`}>
            <input
              type="checkbox"
              checked={settings.maintainAspectRatio}
              onChange={(e) => onUpdateSettings('maintainAspectRatio', e.target.checked)}
              className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 focus:ring-2"
            />
            <span className="text-sm font-medium">Manter proporção original</span>
          </label>
        </div>

        {/* Opção de Remoção de Fundo - Mostrar apenas para formatos suportados */}
                  {showBackgroundRemoval && (
          <div className="flex items-center justify-between py-2">
            <div className="flex flex-col space-y-1">
              <span className="text-sm font-medium">Remover Fundo da Imagem</span>
              <span className="text-sm text-gray-500">Remove automaticamente o fundo da imagem</span>
            </div>
            <button
              onClick={() => onUpdateSettings('removeBackground', !settings.removeBackground)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                settings.removeBackground === true ? 'bg-purple-500' : 'bg-gray-200'
              }`}
            >
              <span
                className={`${
                  settings.removeBackground === true ? 'translate-x-6' : 'translate-x-1'
                } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
              />
            </button>
          </div>
        )}

        {/* Configurações Avançadas */}
        {isAdvancedMode && (
          <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700 animate-slide-in-down">
            {/* Configurações adicionais aqui */}
            <div className={`p-4 rounded-lg ${
              isDarkMode ? 'bg-[#2A2E3F]' : 'bg-gray-50'
            }`}>
              <h5 className={`font-semibold text-sm mb-3 ${
                isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
              }`}>
                Configurações Avançadas
              </h5>
              
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className={`block text-xs font-medium mb-1 ${
                    isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
                  }`}>
                    Algoritmo de Redimensionamento
                  </label>
                  <select className={`w-full px-3 py-2 text-sm rounded-lg border ${
                    isDarkMode 
                      ? 'bg-[#383D52] border-[#4A4F64] text-[#EAECF3]' 
                      : 'bg-white border-[#E5E5E5] text-[#4A4A69]'
                  }`}>
                    <option>Bicúbico (Recomendado)</option>
                    <option>Bilinear</option>
                    <option>Lanczos</option>
                  </select>
                </div>

                <div>
                  <label className={`flex items-center space-x-3 cursor-pointer ${
                    isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
                  }`}>
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 focus:ring-2"
                    />
                    <span className="text-sm">Preservar metadados EXIF</span>
                  </label>
                </div>

                <div>
                  <label className={`flex items-center space-x-3 cursor-pointer ${
                    isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
                  }`}>
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 focus:ring-2"
                    />
                    <span className="text-sm">Otimização progressiva (JPEG)</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 